import _ from 'lodash';
import { Injectable } from '@angular/core';

import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { HttpInterceptorService } from '../../core/services/http-interceptor.service';
import { TestService } from '@ispm/dashboard/test/services/test.service';

@Injectable()
export class AberrationService {

  readonly baseUrl = '/aberration';
  readonly SELECTED_ABERRATIONS_DEFAULT = "SelectedAberrationsDefault";

  public aberrationClassNameObj: any;

  private resourcesMap: any;
  private testTypeId: any;
  private currentRevision: any;
  private secondOpinioner: any;
  private statusIsAfterInitialResults: any;

  constructor(
    private _http: HttpInterceptorService,
    private testService: TestService,
    private authService: AuthenticationService,
  ) {}

  public initModalitiesMap(testConfiguration: any) {
    if (!this.testService.modalitiesData['modalitiesMap']) {
      this.testService.modalitiesData['modalitiesMap'] = { };

      if (testConfiguration.requiredAnnotations) {
        this.testService.modalitiesData['modalitiesMap'] = this.buildModalityMapObject(testConfiguration);
        testConfiguration.requiredAnnotations.forEach((modality) => {
            this.testService.modalitiesData.filterData[modality.type] = {
            lastAppliedFilter: '',
            selectedFilter: { }
            };
        });
      }
    }
  }

  public buildModalityMapObject(testConfiguration: any): any {
    const modalitiesMap = { };

    if (testConfiguration.requiredAnnotations) {
      testConfiguration.requiredAnnotations.forEach((modality) => {
        modalitiesMap[modality.type] = {
          results: {
            aberrations: [],
            opinions: [],
            filteredMessage: []
          },
          itemType: modality.name,
          selectedResults: {
            selectedAberrations: [],
            opinions: []
          }
        };
      });
    }

    return modalitiesMap;
  }

  public setDefaultModality(filters: any, modalities: any, currentModality: any): any {
    let modalityIsDefault: any;
    const defaultModality = _.find(modalities, (modality) => {
      modalityIsDefault = modality.type;

      if (modality.isDefault && !currentModality || modalityIsDefault === currentModality) {
          return modalityIsDefault;
      }
      else if (modality.type === currentModality) {
        return modality;
      }
    });

    const defaultData = {
      defaultModalityName: defaultModality.type,
      defaultModalityFilter: { },
      defaultModalityTypeName: { }
    };

    defaultData.defaultModalityFilter = { };
    defaultData.defaultModalityTypeName = defaultModality.name;

    if (filters) {
      filters.forEach((filter) => {
        if (filter && filter.hasOwnProperty('context')) {
          filter.context.forEach((context) => {
            if (context.resourceType === defaultModality.name && context.reference === defaultModality.type) {
              defaultData.defaultModalityFilter = filter;
              defaultData.defaultModalityTypeName = defaultModality.name;
            }
          });
        }
      });
    }

    return defaultData;
  }

  public getAdditionalResources(testTemplateId: any, revisionId: any, workflowId: any): any {
    return this._http.post(this.baseUrl + '/getAdditionalResources',
    {
      testTemplateId: testTemplateId,
      revisionId: revisionId,
      workflowId: workflowId
    });
  }

  public getAll(
    workflowRequestId: any, revisionId: string, currRevisionId: string, practitionerId: string,
    orderer: any, filterQuery: any, selectedOnly: any, testTypeId: any, modalitiesMap: any, sortBy: any
  ): any {
    return this._http.post(
      this.baseUrl + '/getAberrations',
      {
        workflowRequestId,
        context: revisionId,
        currRevision: currRevisionId,
        practitioner: practitionerId,
        orderer: orderer,
        filterQuery: filterQuery,
        selectedOnly: selectedOnly,
        testTypeId: testTypeId,
        modalitiesMap: modalitiesMap,
        sortBy
      }
    );
  }

  public get(aberrationId: any): any {
    return this._http.get(this.baseUrl + '/' + aberrationId);
  }

  public saveMolecularFinding(revision: any, molecularFinding: any): any {
    return this._http.put(this.baseUrl + '/updateMolecularFinding', {
    revision: revision,
    molecularFinding: molecularFinding
    });
  }

  public saveSelectionBulk(selections: any, opinionId: any, revision?: any): any {
    return this._http.put(this.baseUrl + '/updateSelectionBulk/' + opinionId, { selections: selections, revision: revision });
  }

  public saveState(opinionId: any, state: any, revision: any): any {
    return this._http.put(this.baseUrl + '/updateState/' + opinionId, { state: state, revision: revision });
  }

  public getAberrationClassNames(resourceConfigsAberrationType: any): any {
    const aberrationClassNames = [];
    this.aberrationClassNameObj = { };

    if (resourceConfigsAberrationType && resourceConfigsAberrationType.length > 0) {
      const aberrationTypes = resourceConfigsAberrationType.filter(resourceConfig => resourceConfig.name === 'aberrationType');
      const defaultAbeerationClassNames = resourceConfigsAberrationType.filter(resourceConfig => resourceConfig.isDefault);

      if (defaultAbeerationClassNames && defaultAbeerationClassNames.length > 0) {
        this.aberrationClassNameObj.defaultAberrationClassName = defaultAbeerationClassNames[0].type;
      }

      if (aberrationTypes && aberrationTypes.length > 0) {
        aberrationTypes.forEach(aberrationType => {
          aberrationClassNames.push(aberrationType.type);
        });
      }

      this.aberrationClassNameObj.aberrationClassNames = aberrationClassNames;
    }
    return this.aberrationClassNameObj;
  }

  public exportAberrationsToExcel(testId: any, revisionId: any, maxExportAberrationsInReport: any): any {
    const body = {
      convertToJSON: true,
      exportData: {
        exportSelectedOption: this.SELECTED_ABERRATIONS_DEFAULT,
        performer: this.authService.user.id, /* set current user as the report's performer */
        reportResourceType: 'test',
        reportResourceId: testId,
        parameters: { /* set the specific report's parameters */
          revisionId: revisionId,
          queryMaxAberrations: maxExportAberrationsInReport
        }
      }
    };

    return this._http.post('/report/exportDataToExcel', body);
  }

  // setter and getter for data transfer from test controller to aberrations
  public setResourcesMap(resourcesMap: any) {
    this.resourcesMap = resourcesMap;
  }

  public getResourcesMap(): any {
    return this.resourcesMap;
  }

  public setTestTypeId(testTypeId: any) {
    this.testTypeId = testTypeId;
  }

  public getTestTypeId(): any {
    return this.testTypeId;
  }

  public setCurrentRevision(currentRevision: any) {
    this.currentRevision = currentRevision;
  }

  public getCurrentRevision(): any {
    return this.currentRevision;
  }

  public setSecondOpinioner(secondOpinioner: any) {
    this.secondOpinioner = secondOpinioner;
  }

  public getSecondOpinioner(): any {
    return this.secondOpinioner;
  }

  public setStatusIsAfterInitialResults(statusIsAfterInitialResults: any) {
    this.statusIsAfterInitialResults = statusIsAfterInitialResults;
  }

  public getStatusIsAfterInitialResults(): any {
    return this.statusIsAfterInitialResults;
  }
}
